

import { CompanyRole } from "@/types/CompanyRole";
import {
  changeCompany as changeCompanyApi,
  declineCompanyRole,
  getPaginatedCompanyRoles,
  searchCompanyRoles as searchCompanyRolesApi,
  deleteCompanyRole as deleteCompanyRoleApi,
  createCompanyRole as createCompanyRoleApi,
  createCompanyRolesBatch as createCompanyRolesBatchApi,
  type CompanyInviteOutcome,
  updateCompanyRole as updateCompanyRoleApi,
  switchOwner as switchOwnerApi,
  getCompanyRoleByUserIdAndCompanyId,
  getJoinPreflight,
  JoinPreflightResponse,
  getCompanyMemberRemovalPreview as getCompanyMemberRemovalPreviewApi,
  type CompanyMemberRemovalPreview,
} from "@/lib/api/clients/CompanyRoleClient";
import {

  getCookie,
  setUncriticalCookie,
} from "@/lib/auth/cookies";
import { handleErrorServer, handleErrorServerWithStatus, ServerError } from "@/lib/api/errors/ErrorHandler";
import type { LossesAck } from "@/lib/legal/dissolveLossesAck";

export async function updateCompanyRole(companyRole: CompanyRole) {
  const companyId = await getCookie("companyId");

  try {
    companyRole.companyId = companyId!; // Ensure

    if (!companyRole.companyRoleId)
      throw new Error("Company role ID is required for updating.");

    if (!companyRole.companyId)
      throw new Error("Company ID is required for updating the role.");

    if (!companyRole.role)
      throw new Error("Role is required for updating the company role.");

    if (!companyRole.userId)
      throw new Error("User ID is required for updating the company role.");

    return await updateCompanyRoleApi(companyRole);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/** The company layout's invite: the login company, read from the cookie like every handler here. */
export async function inviteLoginCompanyMembers(
  emails: string[],
  role: string,
): Promise<CompanyInviteOutcome | ServerError> {
  const companyId = await getCookie("companyId");
  try {
    if (!companyId) {
      throw new Error("Company ID is not set in cookies.");
    }
    return await createCompanyRolesBatchApi(companyId, emails, role);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

/**
 * Invite into the company named by `companyId` — passed in, never read from
 * the login cookie here, so an event page can call it for its own company
 * (the backend's `@ValidateCompanyId` refuses any other one).
 */
export async function inviteCompanyMembers(
  companyId: string,
  emails: string[],
  role: string,
): Promise<CompanyInviteOutcome | ServerError> {
  try {
    return await createCompanyRolesBatchApi(companyId, emails, role);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

export async function createCompanyRole(
  email: string,
  role: string,
): Promise<string | ServerError> {
  const companyId = await getCookie("companyId");
  try {
    if (!companyId) {
      throw new Error("Company ID is not set in cookies.");
    }
    return await createCompanyRoleApi(companyId, email, role);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteCompanyRole(
  companyRoleId: string,
): Promise<void | ServerError> {
  const companyId = await getCookie("companyId");

  try {
    if (!companyId) {
      throw new Error("Company ID is not set in cookies.");
    }

    await deleteCompanyRoleApi(companyId, companyRoleId);
  } catch (error) {
    // Keep the HTTP status: a concurrent removal of the same person answers
    // 409 `serverError` (offboarding claim, plan L-4), and the dialog then
    // re-reads the member list (INFO I-1). Same idiom as SubscriptionHandling.
    const serverError = await handleErrorServer(error);
    const shaped = error as { status?: unknown; response?: { status?: unknown } };
    const status = shaped?.response?.status ?? shaped?.status;
    return typeof status === "number" ? { ...serverError, httpStatus: status } : serverError;
  }
}

/**
 * Offboarding: the remove-member preview for the dialog. Any failure — an
 * older backend's 404 included — comes back as a ServerError, which the
 * dialog reads as "unknown" and answers with today's text (L-2).
 */
export async function getCompanyMemberRemovalPreview(
  companyRoleId: string,
): Promise<CompanyMemberRemovalPreview | ServerError> {
  try {
    const companyId = await getCookie("companyId");
    if (!companyId) throw new Error("Company ID is not set in cookies.");
    if (!companyRoleId) throw new Error("validationError");
    return await getCompanyMemberRemovalPreviewApi(companyId, companyRoleId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function switchOwner(
  newOwnerId: string,
): Promise<void | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!companyId) {
      throw new Error("Company ID is not set in cookies.");
    }

    const response = await switchOwnerApi(companyId, newOwnerId);

    // Tokens ride in HttpOnly cookies set by the backend; only
    // companyId is mirrored into localStorage for cockpit-side UI.
    await setUncriticalCookie("companyId", response.companyId, 60 * 60 * 24 * 365);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * @param options.lossesAck PB-CDP-7 — set exactly when the join dialog showed
 *   a loss notice (one-time event plans / balances the dissolve forfeits, or
 *   the generic one) AND the user ticked the box under it; the door refuses
 *   such a switch without it (`acknowledgeLosses=true` + the `lossesAck*`
 *   params, which the backend records).
 */
export async function changeCompany(
  newCompanyId: string,
  options: { lossesAck?: LossesAck | null } = {},
): Promise<void | ServerError> {
  try {
    // TODO refetch if not found
    const initialCompanyId = await getCookie("companyId");

    const response = await changeCompanyApi(
      initialCompanyId!,
      newCompanyId,
      options.lossesAck ?? null,
    );

    await setUncriticalCookie("companyId", response.companyId, 60 * 60 * 24 * 365);
  } catch (error) {
    // SPA: Next's redirect-error mechanism doesn't exist here (no redirect()
    // throws). The legacy guard against that propagation is dropped — every
    // failure surfaces as a normal ServerError.
    return handleErrorServer(error);
  }
}

export async function declineCompanyInvitation(
  companyId: string,
): Promise<void | ServerError> {
  try {

    return await declineCompanyRole(companyId!);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function searchCompanyUsers(q: string, limit: number = 20) {
  try {
    const companyId = await getCookie("companyId");
    if (!companyId) throw new Error("Company ID is not set in cookies.");
    return await searchCompanyRolesApi(companyId, q, limit);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getCompanyUsers(page: number = 1, pageSize: number = 10) {
  try {
    const companyId = await getCookie("companyId");

    if (!companyId) {
      throw new Error("Company ID is not set in cookies.");
    }

    return await getPaginatedCompanyRoles(
      companyId,
      page,
      pageSize,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getJoinPreflightAction(): Promise<JoinPreflightResponse | ServerError> {
  try {
    const companyId = await getCookie("companyId");
    if (!companyId) throw new Error("Company ID is not set in cookies.");
    return await getJoinPreflight(companyId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getCompanyRole(
  fields?: string,
): Promise<CompanyRole | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!companyId) {
      throw new Error("Company ID is not set in cookies.");
    }

    return await getCompanyRoleByUserIdAndCompanyId(
      companyId,
      fields || "",
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}
